import { createSignal } from 'solid-js';
import { ThemeSwitcher } from '../components/ThemeSwitcher';

// Skills data organized by category
const skillsData = [
  {
    category: 'Core Platform & Cloud',
    items: [
      'Kubernetes (CKA/CKS)',
      'Docker',
      'Helm',
      'AWS (EKS, EC2, RDS)',
      'Terraform',
      'Ansible',
      'Puppet',
    ],
    description:
      'Cloud-native platform engineering with self-hosted Kubernetes, configuration management and infrastructure-as-code.',
  },
  {
    category: 'CI/CD & GitOps',
    items: ['GitLab CI', 'ArgoCD', 'Flux', 'Jenkins'],
    description:
      'Modern deployment pipelines with GitOps workflows and progressive delivery strategies.',
  },
  {
    category: 'Security & DevSecOps',
    items: [
      'HashiCorp Vault',
      'Cilium (eBPF)',
      'SonarQube',
      'Veracode',
      'Qualys',
      'mTLS',
      'TDE',
      'Zero-Trust',
    ],
    description:
      'Financial-grade security hardening with secret management, network policies, and compliance.',
  },
  {
    category: 'Data & Messaging',
    items: ['PostgreSQL', 'Kafka', 'Schema Registry', 'ActiveMQ Artemis', 'ETCD', 'Zookeeper'],
    description: 'High-availability database clusters and event streaming platforms.',
  },
  {
    category: 'Edge & CDN',
    items: ['Akamai (CDN/WAF)', 'Global Load Balancing', 'DDoS Protection'],
    description: 'Global edge optimization and web application firewall management.',
  },
  {
    category: 'Languages & Scripting',
    items: ['Python', 'Go', 'Bash'],
    description: 'Automation and tooling development for platform operations.',
  },
];

// Project data for expanded view
const projectsData = [
  {
    id: 'finops-db',
    title: 'FinOps: Database Consolidation',
    outcome: '$1M/year saved',
    description:
      'Consolidated enterprise databases by migrating from Crunchy Postgres to Community Postgres, eliminating expensive licensing overhead while maintaining performance.',
    details:
      'Led the migration from Crunchy Postgres (enterprise licensed) to Community Postgres across multiple environments. This involved careful planning for data migration, connection pooling adjustments, and ensuring zero downtime during cutover. The initiative eliminated recurring licensing costs while maintaining equivalent performance and reliability.',
    tags: ['PostgreSQL', 'FinOps', 'Cost Optimization', 'Database Migration'],
    role: 'Lead DevOps Engineer',
    duration: '3 months',
  },
  {
    id: 'cicd-modernization',
    title: 'CI/CD Modernization & GitOps',
    outcome: '30% faster pipelines',
    description:
      'Migrated legacy CI/CD from Jenkins to GitLab CI with ArgoCD for GitOps-driven deployments. Designed a "base, component, overlay" GitOps architecture for the API management ecosystem.',
    details:
      'Architected and implemented a complete CI/CD transformation, moving from Jenkins to GitLab CI with ArgoCD for GitOps workflows. Designed a "base, component, overlay" GitOps approach for the API management ecosystem to enable off-the-shelf, carton-and-ship setup, along with component versioning and component reusability advantages.',
    tags: ['GitLab CI', 'ArgoCD', 'API Management', 'GitOps'],
    role: 'Senior DevOps Engineer',
    duration: '6 months',
  },
  {
    id: 'ephemeral-k8s',
    title: 'Ephemeral K8s Environments',
    outcome: '4 days → 45 min',
    description:
      'Architected on-demand Kubernetes environments for parallel testing with automated provisioning and teardown, dramatically accelerating feature rollout.',
    details:
      'Designed and built a system for on-demand, isolated Kubernetes environments for feature testing. Engineers can spin up production-like environments in minutes instead of days. Implemented automated cleanup to control costs, namespace isolation for security, and integration with CI/CD for automatic environment provisioning on PR creation.',
    tags: ['Kubernetes', 'Helm', 'DevEx', 'Automation'],
    role: 'DevOps Engineer II',
    duration: '4 months',
  },
  {
    id: 'active-active',
    title: 'Active-Active Architecture',
    outcome: '99.99% uptime target',
    description:
      'Co-designed active-active financial platform architecture. Migrated WAF to Akamai for global edge optimization and 40% latency reduction.',
    details:
      'Collaborated on designing an active-active architecture for critical financial platforms to achieve 99.99% uptime. Led the WAF migration to Akamai, implementing global edge caching and DDoS protection. Resulted in 40% latency reduction for international users and improved resilience against regional failures.',
    tags: ['Akamai', 'WAF', 'High Availability', 'Edge Computing'],
    role: 'Lead DevOps Engineer',
    duration: '8 months',
  },
  {
    id: 'security-hardening',
    title: 'Security Hardening & Compliance',
    outcome: 'Zero-trust security',
    description:
      'Implemented multi-tenant security with HashiCorp Vault, Cilium network policies, mTLS/TDE for financial regulatory compliance.',
    details:
      'Implemented comprehensive security hardening across the platform including HashiCorp Vault for dynamic secret management, Cilium for eBPF-based network policies, mTLS for service-to-service authentication, and Transparent Data Encryption (TDE) for database compliance. Achieved zero-trust architecture meeting strict financial regulations.',
    tags: ['Vault', 'Cilium', 'DevSecOps', 'mTLS', 'TDE'],
    role: 'Senior DevOps Engineer',
    duration: 'Ongoing',
  },
  {
    id: 'cloud-native-platform',
    title: 'Cloud-Native Platform Foundation',
    outcome: 'Foundation for all services',
    description:
      "Built company's first cloud-native shared services platform with Helm charts, Vault integration, and observability stack (Prometheus/Grafana/ELK).",
    details:
      'Established the foundational cloud-native platform that became the standard for all microservices. Created reusable Helm charts, implemented centralized observability with Prometheus, Grafana, and ELK stack, and integrated HashiCorp Vault for secret management. Reduced MTTD by 60% and provided self-service capabilities to development teams.',
    tags: ['Kubernetes', 'Helm', 'Observability', 'Platform Engineering'],
    role: 'Software/DevOps Engineer',
    duration: '12 months',
  },
];

export default function Home() {
  const [expandedProject, setExpandedProject] = createSignal<string | null>(null);

  const openProject = (projectId: string) => setExpandedProject(projectId);
  const closeProject = () => setExpandedProject(null);

  const activeProject = () => projectsData.find(p => p.id === expandedProject());
  return (
    <div class="home">
      <a href="#main-content" class="skip-link">
        Skip to main content
      </a>

      <header class="header">
        <div class="container">
          <nav class="header__nav" aria-label="Main navigation">
            <a href="/" class="header__logo" aria-label="Rohith - Home">
              Rohith
            </a>
            <ThemeSwitcher />
          </nav>
        </div>
      </header>

      <main id="main-content" class="main">
        <section class="hero" aria-labelledby="hero-heading">
          <div class="container">
            <div class="hero__grid">
              <div class="hero__content">
                <span class="hero__greeting">Hello 👋</span>
                <h1 id="hero-heading" class="hero__title">
                  I'm Rohith
                </h1>
                <p class="hero__role">Engineer, DevOps & Platform Reliability</p>
                <p class="hero__description">
                  Based in Bangalore, I build reliable infrastructure and pipelines that help teams
                  ship faster and sleep better at night.
                </p>
                <div class="hero__actions">
                  <a href="mailto:contact@rohithp.com" class="btn btn--primary">
                    Get in Touch
                  </a>
                  <a
                    href="https://www.linkedin.com/in/rohithp"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn btn--ghost"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>

              <nav class="bento-grid" aria-label="Quick navigation">
                <a href="#about" class="bento-card card card--interactive">
                  <div class="bento-card__content">
                    <span class="bento-card__title">About me</span>
                    <span class="bento-card__subtitle">Learn more</span>
                  </div>
                  <div class="bento-card__action" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </a>

                <a href="#projects" class="bento-card card card--interactive">
                  <div class="bento-card__content">
                    <span class="bento-card__title">My Projects</span>
                    <span class="bento-card__subtitle">View work</span>
                  </div>
                  <div class="bento-card__action" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </a>

                <a href="#skills" class="bento-card card card--interactive">
                  <div class="bento-card__content">
                    <span class="bento-card__title">Skills</span>
                    <span class="bento-card__subtitle">Expertise</span>
                  </div>
                  <div class="bento-card__action" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </a>

                <a href="#contact" class="bento-card card card--interactive">
                  <div class="bento-card__content">
                    <span class="bento-card__title">Contact me</span>
                    <span class="bento-card__subtitle">Get in touch</span>
                  </div>
                  <div class="bento-card__action" aria-hidden="true">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                  </div>
                </a>
              </nav>
            </div>
          </div>
        </section>

        {/* Metrics Section */}
        <section id="metrics" class="rohith-metric-section" aria-labelledby="metrics-heading">
          <div class="container">
            <h2 id="metrics-heading" class="rohith-metric-title">
              By the Numbers
            </h2>
            <div class="rohith-metric-grid">
              <article class="metric">
                <span class="metric-value">$1.5M+</span>
                <span class="metric-label">Cost Savings via FinOps</span>
              </article>
              <article class="metric">
                <span class="metric-value">98%</span>
                <span class="metric-label">Release Latency Reduction</span>
              </article>
              <article class="metric">
                <span class="metric-value">5+</span>
                <span class="metric-label">Years at Bottomline</span>
              </article>
              <article class="metric">
                <span class="metric-value">9</span>
                <span class="metric-label">Engineers Led</span>
              </article>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" class="rohith-about-section" aria-labelledby="about-heading">
          <div class="container">
            <h2 id="about-heading" class="rohith-about-title">
              About Me
            </h2>
            <div class="rohith-about-layout">
              {/* Left Column - About Text */}
              <div class="rohith-about-main">
                <p class="rohith-about-text">
                  I'm a DevOps Engineer at Bottomline, building resilient financial infrastructure.
                  I specialize in cloud-native platforms, FinOps, and Site Reliability.
                </p>

                <p class="rohith-about-text">
                  I run a self-hosted Kubernetes homelab because breaking things at home is cheaper
                  than breaking them at work.
                </p>

                <p class="rohith-about-text">
                  Into RTS games-<em>Age of Empires</em>, <em>Total War</em>-the complexity, the
                  strategy, the chaos. Also <em>Halo</em>, because it is the greatest dystopian
                  sci-fi story ever told.
                </p>

                <p class="rohith-about-text">
                  I listen to rock and metal for the politics. Revolutionary riffs and dissent that
                  questions authority. Loud, heavy, and meaningful.
                </p>
              </div>

              {/* Right Column - Currently Playing/Listening */}
              <aside class="rohith-about-sidebar">
                <div class="rohith-sidebar-section">
                  <h3 class="rohith-about-subtitle">Currently Playing</h3>
                  <div class="rohith-game-grid">
                    <a
                      href="https://store.steampowered.com/app/3321460/Crimson_Desert/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rohith-game-card"
                      style="background-image: url('https://images.igdb.com/igdb/image/upload/t_cover_big/coaqai.webp')"
                      aria-label="Crimson Desert - 5 out of 5 stars"
                    >
                      <div class="rohith-game-overlay">
                        <h4 class="rohith-game-title">Crimson Desert</h4>
                        <div class="rohith-game-rating">
                          <span class="rohith-game-stars">★★★★★</span>
                          <span class="rohith-game-score">5/5</span>
                        </div>
                      </div>
                    </a>

                    <a
                      href="https://store.steampowered.com/app/2807960/Battlefield_6/"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rohith-game-card"
                      style="background-image: url('https://images.igdb.com/igdb/image/upload/t_cover_big/coa5zt.webp')"
                      aria-label="Battlefield 6 on Steam - 4 out of 5 stars"
                    >
                      <div class="rohith-game-overlay">
                        <h4 class="rohith-game-title">Battlefield 6</h4>
                        <div class="rohith-game-rating">
                          <span class="rohith-game-stars">★★★★☆</span>
                          <span class="rohith-game-score">4/5</span>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>

                <div class="rohith-sidebar-section">
                  <h3 class="rohith-about-subtitle">Currently Listening</h3>
                  <div class="rohith-music-list">
                    <a
                      href="https://open.spotify.com/playlist/37i9dQZF1EIgsBucdAI1u5"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rohith-music-item"
                      aria-label="Hard Rock Metal Mix on Spotify"
                    >
                      <div class="rohith-music-icon" aria-hidden="true">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      </div>
                      <div class="rohith-music-info">
                        <h4 class="rohith-music-artist">Hard Rock Metal Mix</h4>
                        <span class="rohith-music-genre">Spotify Playlist</span>
                      </div>
                    </a>

                    <a
                      href="https://open.spotify.com/playlist/37i9dQZF1EIefLxrHQP8p4"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rohith-music-item"
                      aria-label="Alternative Rock Mix on Spotify"
                    >
                      <div class="rohith-music-icon" aria-hidden="true">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      </div>
                      <div class="rohith-music-info">
                        <h4 class="rohith-music-artist">Alternative Rock Mix</h4>
                        <span class="rohith-music-genre">Spotify Playlist</span>
                      </div>
                    </a>

                    <a
                      href="https://open.spotify.com/playlist/37i9dQZF1DZ06evO34PI4g"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="rohith-music-item"
                      aria-label="This is System Of A Down on Spotify"
                    >
                      <div class="rohith-music-icon" aria-hidden="true">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M9 18V5l12-2v13" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      </div>
                      <div class="rohith-music-info">
                        <h4 class="rohith-music-artist">This is System Of A Down</h4>
                        <span class="rohith-music-genre">Spotify Playlist</span>
                      </div>
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" class="rohith-project-section" aria-labelledby="projects-heading">
          <div class="container">
            <h2 id="projects-heading" class="rohith-project-title">
              Featured Projects
            </h2>
            <div class="rohith-project-grid">
              {projectsData.map(project => (
                <article
                  class="rohith-project-card"
                  role="button"
                  tabindex="0"
                  aria-label={`View ${project.title} project details`}
                  onClick={() => openProject(project.id)}
                  onKeyDown={e => e.key === 'Enter' && openProject(project.id)}
                >
                  <div class="rohith-project-header">
                    <h3 class="rohith-project-name">{project.title}</h3>
                    <span class="rohith-project-outcome">{project.outcome}</span>
                  </div>
                  <p class="rohith-project-description">{project.description}</p>
                  <div class="rohith-project-tags">
                    {project.tags.slice(0, 3).map(tag => (
                      <span class="rohith-project-tag">{tag}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Project Detail Modal */}
        {expandedProject() && (
          <div
            class="rohith-project-modal-overlay"
            onClick={closeProject}
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-modal-title"
          >
            <div class="rohith-project-modal" onClick={e => e.stopPropagation()}>
              <button
                class="rohith-project-modal-close"
                onClick={closeProject}
                aria-label="Close project details"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              {activeProject() && (
                <>
                  <div class="rohith-project-modal-header">
                    <span class="rohith-project-modal-outcome">{activeProject()?.outcome}</span>
                    <h3 id="project-modal-title" class="rohith-project-modal-title">
                      {activeProject()?.title}
                    </h3>
                  </div>
                  <div class="rohith-project-modal-meta">
                    <span class="rohith-project-meta-item">
                      <strong>Role:</strong> {activeProject()?.role}
                    </span>
                    <span class="rohith-project-meta-item">
                      <strong>Duration:</strong> {activeProject()?.duration}
                    </span>
                  </div>
                  <div class="rohith-project-modal-content">
                    <p>{activeProject()?.details}</p>
                  </div>
                  <div class="rohith-project-modal-tags">
                    {activeProject()?.tags.map(tag => (
                      <span class="rohith-project-tag rohith-project-tag--large">{tag}</span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Skills Section */}
        <section id="skills" class="rohith-skill-section" aria-labelledby="skills-heading">
          <div class="container">
            <h2 id="skills-heading" class="rohith-skill-title">
              Skills & Expertise
            </h2>
            <div class="rohith-skill-grid">
              {skillsData.map(skillCategory => (
                <article class="rohith-skill-card">
                  <h3 class="rohith-skill-category">{skillCategory.category}</h3>
                  <p class="rohith-skill-description">{skillCategory.description}</p>
                  <div class="rohith-skill-items">
                    {skillCategory.items.map(item => (
                      <span class="rohith-skill-item">{item}</span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications Section */}
        <section
          id="certifications"
          class="rohith-cert-section"
          aria-labelledby="certifications-heading"
        >
          <div class="container">
            <h2 id="certifications-heading" class="rohith-cert-title">
              Certifications
            </h2>
            <div class="rohith-cert-grid">
              <article class="rohith-cert-card">
                <div class="rohith-cert-badge">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <rect width="48" height="48" rx="8" fill="var(--accent)" />
                    <text
                      x="24"
                      y="30"
                      text-anchor="middle"
                      fill="var(--accent-fg)"
                      font-size="16"
                      font-weight="bold"
                    >
                      CKS
                    </text>
                  </svg>
                </div>
                <div class="rohith-cert-content">
                  <h3 class="rohith-cert-name">Certified Kubernetes Security Specialist</h3>
                  <p class="rohith-cert-issuer">Linux Foundation</p>
                  <p class="rohith-cert-id">ID: LF-5k7ngcpyt2</p>
                  <a
                    href="https://www.credly.com/badges/a4f933d8-90e5-47e7-abdf-76b12bb60c55/public_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="rohith-cert-link"
                  >
                    Verify on Credly →
                  </a>
                </div>
              </article>

              <article class="rohith-cert-card">
                <div class="rohith-cert-badge">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <rect width="48" height="48" rx="8" fill="var(--accent)" />
                    <text
                      x="24"
                      y="30"
                      text-anchor="middle"
                      fill="var(--accent-fg)"
                      font-size="16"
                      font-weight="bold"
                    >
                      CKA
                    </text>
                  </svg>
                </div>
                <div class="rohith-cert-content">
                  <h3 class="rohith-cert-name">Certified Kubernetes Administrator</h3>
                  <p class="rohith-cert-issuer">Linux Foundation</p>
                  <p class="rohith-cert-id">ID: LF-9cbjnt2i69</p>
                  <a
                    href="https://www.credly.com/badges/1813a71c-22a1-4a8a-8746-c50020adca08/public_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="rohith-cert-link"
                  >
                    Verify on Credly →
                  </a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="contact" class="section" aria-labelledby="contact-heading">
          <div class="container">
            <h2 id="contact-heading">Contact</h2>
            <p>
              Email: <a href="mailto:contact@rohithp.com">contact@rohithp.com</a>
            </p>
            <p>
              LinkedIn:{' '}
              <a
                href="https://www.linkedin.com/in/rohithp"
                target="_blank"
                rel="noopener noreferrer"
              >
                linkedin.com/in/rohithp
              </a>
            </p>
          </div>
        </section>
      </main>

      <footer class="footer">
        <div class="container">
          <div class="footer__content">
            <p>© {new Date().getFullYear()} Rohith. All rights reserved.</p>
            <div class="footer__social">
              <a
                href="https://www.linkedin.com/in/rohithp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a href="mailto:contact@rohithp.com" aria-label="Email">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
